import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.ts';
import type { ActiveRunRow, ActivityRow, DeckActivityPayload } from '../../shared/api.ts';

// Two cadences. While a run is in flight we want its progress promptly; when
// nothing is running we still check occasionally, because a run started in
// ANOTHER tab is invisible until we ask. The endpoint reads a couple of small
// files, so the idle tick is cheap.
const RUNNING_POLL_MS = 5000;
const IDLE_POLL_MS = 20_000;

/**
 * Per-deck activity (GET /api/chat/:slug/history) — two halves on one fetch:
 *
 *  - `rows`: durable AI cards across the deck's chat threads, with status,
 *    previewing flag and revertibility.
 *  - `running`: Claude turns the SERVER currently has in flight. This used to
 *    be browser-tab state only, so refreshing the page (or opening a second
 *    tab) showed an idle deck while an agent was still writing slides — half
 *    of the "it vanished from activity" report, 2026-07-30.
 *
 * Refreshes on mount, on demand (refresh()), whenever anything dispatches the
 * `studio:activity-refresh` window event, and on a short timer while a run is
 * live (nothing else would tell us a headless run had finished).
 */
/** What the hook holds: the deck's activity rows plus the server's in-flight
 *  runs, and a manual refetch. Both lists are optional because the first
 *  render has neither, and every reader defends with `|| []`. */
export type DeckActivity = {
  rows?: ActivityRow[];
  running?: ActiveRunRow[];
  refresh: () => void;
};

export function useDeckActivity(slug: string | null | undefined) {
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [running, setRunning] = useState<ActiveRunRow[]>([]);
  // Read by the poll loop without making `refresh` depend on it.
  const runningRef = useRef(0);

  const refresh = useCallback(() => {
    if (!slug) return;
    api
      .get<Partial<DeckActivityPayload>>(`/api/chat/${slug}/history`)
      .then((res) => {
        setRows(Array.isArray(res?.rows) ? res.rows : []);
        const live = Array.isArray(res?.running) ? res.running : [];
        runningRef.current = live.length;
        setRunning(live);
      })
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    refresh();
    window.addEventListener('studio:activity-refresh', refresh);
    return () => window.removeEventListener('studio:activity-refresh', refresh);
  }, [refresh]);

  useEffect(() => {
    let lastIdleTick = Date.now();
    const timer = setInterval(() => {
      if (document.hidden) return; // a background tab needn't poll at all
      if (runningRef.current > 0) {
        refresh();
        return;
      }
      if (Date.now() - lastIdleTick < IDLE_POLL_MS) return;
      lastIdleTick = Date.now();
      refresh();
    }, RUNNING_POLL_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  return { rows, running, refresh };
}

/** Tell every mounted useDeckActivity to refetch (after an apply/reject/revert). */
export function notifyActivityChanged() {
  window.dispatchEvent(new Event('studio:activity-refresh'));
}
